import { Link } from "react-router-dom";
import { Mailbox, ArrowLeft } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <Link to="/" className="mb-8 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to MailShift
      </Link>

      <div className="mb-10 flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-white">
          <Mailbox className="h-5 w-5" />
        </div>
        <p className="font-heading text-xl font-bold tracking-tighter">MailShift</p>
      </div>

      <h1 className="font-heading text-4xl font-bold tracking-tighter">Terms of Service</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

      <div className="prose prose-slate mt-10 max-w-none space-y-6 text-sm leading-relaxed text-foreground">
        <section>
          <p>These Terms of Service ("Terms") govern your access to and use of MailShift (the "Service"), operated by us ("MailShift", "we", "us"). By creating an account or using the Service, you agree to these Terms. If you don't agree, please don't use the Service.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">1. The Service</h2>
          <p>MailShift lets you migrate email messages, folders, and related data between mailbox providers that you own or are authorized to access, using IMAP and/or OAuth-based provider sign-in (such as Google or Microsoft).</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">2. Your account</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>You must provide accurate information when creating an account and keep your password secure.</li>
            <li>You're responsible for all activity that happens under your account.</li>
            <li>You must be at least 16 years old to use the Service.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">3. Authorization to access mailboxes</h2>
          <p>You represent and warrant that you own, or have proper authorization from the owner of, every source and destination mailbox you connect to MailShift. You are solely responsible for ensuring you have the legal right to access and migrate the data in question. MailShift is a tool that acts on your instructions — we do not review or verify your authorization to access any mailbox you connect.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">4. Acceptable use</h2>
          <p>You agree not to use the Service to:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Access, or attempt to access, any mailbox you are not authorized to access.</li>
            <li>Violate any applicable law or the terms of service of any third-party mailbox provider.</li>
            <li>Transmit malware, or interfere with or disrupt the Service or its infrastructure.</li>
            <li>Reverse engineer or resell the Service without our written permission.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">5. Plans, billing, and refunds</h2>
          <p>Some features of the Service require a paid plan. Prices, quotas, and features for each plan are shown on our Pricing page and may change with notice. Payments are processed by our third-party payment provider. Unless required by law or stated otherwise at the time of purchase, fees are non-refundable once a migration has started.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">6. Service availability</h2>
          <p>We aim to keep the Service available and reliable, but we don't guarantee uninterrupted access. Migrations depend on third-party mailbox providers (such as Google and Microsoft), whose availability, rate limits, and policies are outside our control and may affect migration speed or success.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">7. Data and content</h2>
          <p>You retain all rights to your email content. We access your mailbox data only as needed to perform the migration you request, as described in our <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>. We do not claim ownership of your email content.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">8. Disclaimer of warranties</h2>
          <p>The Service is provided "as is" and "as available," without warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that migrations will be error-free or that every message will transfer successfully, given the dependency on third-party mail servers.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">9. Limitation of liability</h2>
          <p>To the maximum extent permitted by law, MailShift and its team will not be liable for any indirect, incidental, special, or consequential damages, or for any loss of data, profits, or business, arising from your use of the Service. Our total liability for any claim relating to the Service will not exceed the amount you paid us in the 3 months preceding the claim.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">10. Termination</h2>
          <p>You may stop using the Service and delete your account at any time. We may suspend or terminate accounts that violate these Terms, misuse the Service, or pose a security risk.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">11. Changes to these Terms</h2>
          <p>We may update these Terms from time to time. We'll post the updated version here with a revised "Last updated" date. Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.</p>
        </section>

        <section>
          <h2 className="font-heading text-xl font-bold">12. Contact us</h2>
          <p>Questions about these Terms? Contact us at <a href="mailto:support@mailshifts.com" className="text-primary underline">support@mailshifts.com</a>.</p>
        </section>
      </div>
    </div>
  );
}
